<template>
  <div class="vg-confirmation-recap">
    <template v-if="!isCancelled">
      <div
        v-for="(item, index) in cart?.items"
        :key="item.externalOrderLineId || item.choice?.id || index"
        class="order-summary__item"
      >
        <div
          class="order-summary__item-header vg-confirmation-recap__item-header"
        >
          <div class="order-summary__line-name">
            <span v-if="Number(item.qty) > 1" class="vg-confirmation-recap__qty"
              >{{ item.qty }} ×</span
            >
            <template v-if="order.orderType === 'crosssell'">
              <span
                class="product-name"
                v-html="renderHTML(item.choice.vanguardLabel)"
              ></span>
            </template>
            <template v-else>
              <span v-if="lineName(item)" class="product-name">{{
                lineName(item)
              }}</span>
            </template>
          </div>
          <div
            v-if="showLinePrice(item)"
            class="vg-confirmation-recap__line-price"
            :class="{
              'vg-confirmation-recap__line-price--starting': hasStartingPrice(
                resolveChoice(item),
              ),
            }"
          >
            <ChoicePrice
              v-if="hasStartingPrice(resolveChoice(item))"
              :choice="resolveChoice(item)"
              :compact="true"
            />
            <template v-else>
              <span class="vg-confirmation-recap__line-amount">{{
                lineAmount(item)
              }}</span>
              <span class="vg-confirmation-recap__line-currency">€</span>
            </template>
          </div>
        </div>

        <div
          v-if="freeDaysMention(item)"
          class="vg-confirmation-recap__free-days"
        >
          <span class="promotion-label">{{ freeDaysMention(item) }}</span>
        </div>

        <div
          v-if="!hasStartingPrice(resolveChoice(item)) && lineDetail(item)"
          class="order-summary__description choice-description"
        >
          {{ lineDetail(item) }}
        </div>

        <p v-if="item.customText" class="vg-confirmation-recap__meta">
          <span class="vg-confirmation-recap__meta-label"
            >{{ $t("labels.customText") }}:</span
          >
          <span
            :class="[
              'vg-confirmation-recap__meta-value',
              'item-custom-text',
              lowerCaseAndJoin(item.customFont),
            ]"
            >{{ item.customText }}</span
          >
        </p>

        <p v-if="item.productInfos" class="vg-confirmation-recap__meta">
          <span class="vg-confirmation-recap__meta-value item-product-infos">{{
            item.productInfos
          }}</span>
        </p>

        <div
          v-if="
            !hasStartingPrice(resolveChoice(item)) &&
            item.choice.bcl &&
            item.choice.hasOwnProperty('maxTermNumber') &&
            item.choice?.maxTermNumber > 0
          "
          class="vg-confirmation-recap__bcl choice-description-bcl"
        >
          <p class="vg-confirmation-recap__bcl-line">
            <span class="choice-description-bcl-label-then">{{
              $t("labels.then").toLowerCase()
            }}</span>
            <span class="choice-description-bcl-duration">{{
              item.choice.maxTermNumber - 1
            }}</span>
            <span class="choice-description-bcl-separator">×</span>
            <span class="choice-description-bcl-price">{{
              formatPrice(item.choice.defaultPrice)
            }}</span>
            <span class="choice-description-bcl-currency">€</span>
            <span class="choice-description-bcl-by">
              /{{
                item.choice.renawalTermLength > 1
                  ? `${item.choice.renawalTermLength}${$t("labels.months")}`
                  : $t("labels.month")
              }}</span
            >
          </p>
          <p class="choice-description-bcl-next-payment">
            {{ $t("labels.nextPayment") }}
            {{ item.choice.renewalTermLength }}
            {{
              item.choice.renewalTermLength > 1
                ? $t("labels.months")
                : $t("labels.month")
            }}
          </p>
        </div>
      </div>

      <ul
        v-if="cart?.items?.length"
        class="cart-totals vg-confirmation-recap__totals"
        :class="{ 'cart-totals--order-page': isOrderPage }"
      >
        <li
          v-if="subTotal && shouldShowShippingDetails"
          class="cart-totals__row cart-totals__row--subtotal subtotal"
        >
          <span class="cart-totals__label label">{{
            $t("labels.subTotal")
          }}</span>
          <span class="cart-totals__value">
            <span class="cart-totals__amount subtotal">{{ subTotal }}</span>
            <span class="cart-totals__currency currency">€</span>
          </span>
        </li>
        <li
          v-if="shippingPrice && shouldShowShippingDetails"
          class="cart-totals__row cart-totals__row--shipping shipping-fees"
        >
          <span class="cart-totals__label label">{{
            $t("labels.shippingFees")
          }}</span>
          <span class="cart-totals__value">
            <span class="cart-totals__amount amount">{{ shippingPrice }}</span>
            <span class="cart-totals__currency currency">€</span>
          </span>
        </li>
        <li
          v-for="coupon in couponList"
          :key="coupon.code"
          class="cart-totals__row cart-totals__row--discount coupons"
        >
          <span class="cart-totals__label label">
            <span class="cart-totals__discount-code">{{ coupon.code }}</span>
            <span
              v-if="coupon.discountAmount && coupon.isPercentage"
              class="cart-totals__discount-pct"
            >
              (-{{ coupon.discountAmount }}%)
            </span>
          </span>
          <span v-if="coupon.discountedAmount" class="cart-totals__value">
            <span
              class="cart-totals__amount amount cart-totals__amount--discount"
              >-{{ formatPrice(coupon.discountedAmount) }}</span
            >
            <span class="cart-totals__currency currency">€</span>
          </span>
        </li>
        <li
          v-if="freeDaysCount"
          class="cart-totals__row cart-totals__row--today"
        >
          <span class="cart-totals__label label">{{
            $t("labels.paymentAtOrderTime")
          }}</span>
          <span class="cart-totals__value">
            <span class="cart-totals__amount">{{ formatPrice(0) }}</span>
            <span class="cart-totals__currency currency">€</span>
          </span>
        </li>
        <li
          v-if="freeDaysCount"
          class="cart-totals__row cart-totals__row--total cart-totals__row--after-free-days total"
        >
          <span class="cart-totals__label label">{{
            $t("labels.paymentAfterFreeDays", { n: freeDaysCount })
          }}</span>
          <span class="cart-totals__value">
            <span class="cart-totals__amount subtotal">{{ total }}</span>
            <span class="cart-totals__currency currency">€</span>
          </span>
        </li>
        <li
          v-else-if="total"
          class="cart-totals__row cart-totals__row--total total"
        >
          <span class="cart-totals__label label">{{ $t("labels.total") }}</span>
          <span class="cart-totals__value">
            <span class="cart-totals__amount subtotal">{{ total }}</span>
            <span class="cart-totals__currency currency">€</span>
          </span>
        </li>
      </ul>
    </template>

    <CancelOrderLink
      v-if="order"
      :order="order"
      :step="currentStep"
      @cancelled="isCancelled = true"
    />
  </div>
</template>

<script>
import { useOrderHistoryStore } from "@/store/orderHistory";
import CartService from "@/services/cart";
import Shipping from "@/services/shipping";
import Price from "@/services/price";
import Utils from "@/services/utils";
import { choiceCatalogEntry } from "@/services/orderPageChoiceCatalog";
import { stripDecodedHtml } from "@/services/text";
import CancelOrderLink from "@/components/checkout/CancelOrderLink.vue";
import ChoicePrice from "@/components/choice/Price.vue";
import PriceDisplay from "@/services/priceDisplay";
import {
  isOrderPageContext,
  getOrderPageContextVersion,
} from "@/services/orderPageScope";

export default {
  name: "CheckoutOrderRecap",
  components: {
    CancelOrderLink,
    ChoicePrice,
  },
  props: {
    showSubtotalIfFreeShipping: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      order: null,
      cart: null,
      shippingPrice: 0,
      isCancelled: false,
      onVueAppMounted: null,
    };
  },
  mounted() {
    this.onVueAppMounted = () => {
      this.syncHistoryItem();
    };
    document.addEventListener("vue-app-mounted", this.onVueAppMounted);

    useOrderHistoryStore().ensureCurrentOrderScope();
    CartService.emptyCart();
    this.syncHistoryItem();
  },
  beforeUnmount() {
    document.removeEventListener("vue-app-mounted", this.onVueAppMounted);
  },
  computed: {
    isOrderPage() {
      getOrderPageContextVersion().value;
      return isOrderPageContext();
    },
    historyLastUpdate() {
      return useOrderHistoryStore().lastUpdate;
    },
    currentStep() {
      return this.getHistoryItem()?.step || 0;
    },
    subTotal() {
      if (this?.cart?.defaultSubtotal) {
        return this.formatPrice(
          this.cart.defaultSubtotal
            ? this.cart.defaultSubtotal
            : this.cart.subtotal,
        );
      }
    },
    total() {
      if (this?.cart?.total) {
        return this.formatPrice(this.cart.total);
      }
    },
    couponList() {
      const coupons = this?.cart?.coupons;
      if (!coupons) {
        return [];
      }
      return Object.values(coupons);
    },
    isFreeShipping() {
      if (!this?.cart?.items) {
        return true;
      }
      return this.getRawShippingPriceCents() === 0;
    },
    shouldShowShippingDetails() {
      return this.showSubtotalIfFreeShipping || !this.isFreeShipping;
    },
    freeDaysCount() {
      const items = this.cart?.items || [];
      if (items.length !== 1) {
        return 0;
      }
      return PriceDisplay.freeDaysCount(this.resolveChoice(items[0]));
    },
  },
  watch: {
    historyLastUpdate: {
      handler() {
        this.syncHistoryItem();
      },
    },
  },
  methods: {
    syncHistoryItem() {
      const historyItem = this.getHistoryItem();

      if (!historyItem) {
        this.order = null;
        this.cart = null;
        this.shippingPrice = 0;
        return;
      }

      this.order = historyItem.order;
      this.cart = historyItem.cart;
      this.shippingPrice = this.getShippingPrice(historyItem);
    },
    getHistoryItem() {
      return useOrderHistoryStore().activeLastOrderHistoryItem;
    },
    getShippingPrice(historyItem = null) {
      const cart = historyItem?.cart || this.cart;
      const order = historyItem?.order || this.order;

      if (!cart?.items || !order) {
        return 0;
      }

      return this.formatPrice(this.getRawShippingPriceCents(cart, order));
    },
    getRawShippingPriceCents(cart = this.cart, order = this.order) {
      if (!cart?.items || !order) {
        return 0;
      }

      if (
        typeof cart.shippingPrice === "number" &&
        !Number.isNaN(cart.shippingPrice)
      ) {
        return cart.shippingPrice;
      }

      return Shipping.getShippingPriceTotal(cart.items, order.shippingCountry);
    },
    formatPrice(price) {
      return Price.format(price);
    },
    lowerCaseAndJoin(str) {
      return Utils.lowerCaseAndJoin(str);
    },
    isOrderPageContext() {
      return window?.globalConfig?.isOrderPage === true;
    },
    resolveChoice(item) {
      return (
        choiceCatalogEntry(item, { orderPage: this.isOrderPageContext() }) ||
        item?.choice
      );
    },
    lineName(item) {
      const choice = this.resolveChoice(item);
      const fromChoice = stripDecodedHtml(
        choice?.vanguardLabel || choice?.name || "",
      );
      if (fromChoice) {
        return fromChoice;
      }
      if (!this.isOrderPageContext()) {
        return stripDecodedHtml(item?.name || "");
      }
      return "";
    },
    lineDetail(item) {
      const choice = this.resolveChoice(item);
      const detail = stripDecodedHtml(
        choice?.vanguardDescription ||
          choice?.vanguardDescriptionDetail ||
          choice?.description ||
          "",
      );
      const name = this.lineName(item);
      if (!detail || detail === name) {
        return "";
      }
      return detail;
    },
    hasStartingPrice(choice) {
      return (
        choice &&
        !choice.isSingleProduct &&
        Object.prototype.hasOwnProperty.call(choice, "startingPrice") &&
        choice.startingPriceDuration > 0
      );
    },
    showLinePrice(item) {
      const choice = this.resolveChoice(item);
      return Boolean(this.hasStartingPrice(choice) || this.lineAmount(item));
    },
    lineAmount(item) {
      if (!item) {
        return "";
      }

      let amount = item.subtotal;
      if (typeof amount !== "number" || Number.isNaN(amount)) {
        const choice = this.resolveChoice(item) || {};
        const qty = item.qty || 1;
        let unit;
        if (
          Object.prototype.hasOwnProperty.call(choice, "startingPrice") &&
          choice.startingPriceDuration > 0
        ) {
          unit = choice.startingPrice;
        } else {
          unit = choice.defaultPrice ?? choice.unitPrice;
        }
        if (typeof unit !== "number" || Number.isNaN(unit)) {
          return "";
        }
        amount = qty * unit;
      }

      return this.formatPrice(amount);
    },
    freeDaysMention(item) {
      const choice = this.resolveChoice(item);
      if (!PriceDisplay.isTrialMention(choice)) {
        return null;
      }
      return PriceDisplay.resolvePromotionLabel(
        choice,
        this.$i18n?.locale || "fr",
      );
    },
  },
};
</script>
